import { useEffect, useState } from 'react';
import type { EstimateResponse } from '@volt/shared';
import { ApiError } from '../api/client.js';
import { reportClientError } from '../lib/errorReporter.js';
import { useSession } from '../state/session.js';

export interface EstimateState {
  estimate: EstimateResponse | null;
  loading: boolean;
}

/** Debounced price estimate for the composer's model selector. */
export function useEstimate(
  modelId: string | undefined,
  text: string,
  conversationId?: string,
): EstimateState {
  const { api } = useSession();
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!modelId || text.trim() === '') {
      setEstimate(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      api
        .estimate({ modelId, text, conversationId }, controller.signal)
        .then((result) => {
          if (!cancelled) setEstimate(result);
        })
        .catch((error: unknown) => {
          // Aborted debounce requests are expected and should stay silent.
          if (controller.signal.aborted || cancelled) return;
          if (error instanceof ApiError && error.status === 0) {
            reportClientError({
              code: 'ESTIMATE_TRANSPORT',
              message: error.message || 'Estimate request transport failed',
              route: '/chat/estimate',
              metadata: {
                stage: 'estimate',
                modelId,
                conversationId: conversationId ?? null,
                errorCode: error.code,
                status: error.status,
              },
            });
          }
          /* transient failure — keep the previous estimate */
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [api, modelId, text, conversationId]);

  return { estimate, loading };
}
